import AuthorizationLocalStore from 'frontcore/domain/authorization/store/AuthorizationLocalStore'
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios'
import HttpStatusCode from 'frontcore/data/api-service/http-status-code'
import {
  APIServiceConfiguration,
  APIServiceResponse,
  IAPIService,
  IAPIServiceRequest,
  ResponseType,
} from 'frontcore/data/api-service/types'
import {
  APIServiceConfigurationError,
  APIServiceError,
} from 'frontcore/data/api-service/service-error/errors/service-error'
import { APIServiceErrorFactory } from 'frontcore/data/api-service/service-error/factory'
import { getLanguage } from 'frontcore/i18n'
import { getUrlQueryParameterFromObject } from 'frontcore/utils/communications/apiClient'
import {
  PasswordExpiredError,
  TokenExpiredServiceError,
} from 'frontcore/data/api-service/service-error/errors/authorization-errors'


let configuration: APIServiceConfiguration

const defaultErrorHandler = (e: unknown): void => {
  try {
    sessionErrorHandler(e)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw APIServiceErrorFactory.build(error.response)
    }
    else if (error !== null && typeof error === 'object' && 'request' in error) {
      throw new APIServiceConfigurationError('[APIService]: no response from server')
    } else {
      throw new APIServiceConfigurationError('[APIService]: error while building the request')
    }  
  }
}

export const sessionErrorHandler = (error: unknown): void => {
  if (
    error instanceof TokenExpiredServiceError ||
    error instanceof PasswordExpiredError ||
    (
      error instanceof APIServiceError &&
      error.status === HttpStatusCode.UNAUTHORIZED
    )
  ) {
    const authStorage = new AuthorizationLocalStore()
    authStorage.clear()
    window.location.replace(configuration.unauthorizedRedirectPath)
  }
  throw error
}

class APIServiceRequest implements IAPIServiceRequest {

  configuration: AxiosRequestConfig = {}
  instance: AxiosInstance

  constructor (baseURL: string, headers: AxiosRequestHeaders) {
    this.configuration.headers = headers
    this.instance = axios.create({ baseURL: baseURL })
    this.instance.interceptors.response.use(undefined, defaultErrorHandler)
  }

  withAuthToken (token: string): IAPIServiceRequest {
    if (this.configuration.headers === undefined) {
      throw new APIServiceConfigurationError('[APIService]: missing request headers')
    }
    this.configuration.headers['Authorization'] = `Token ${token}`
    return this
  }

  withSessionToken (): IAPIServiceRequest {
    if (this.configuration.headers === undefined) {
      throw new APIServiceConfigurationError('[APIService]: missing request headers')
    }
    const authStorage = new AuthorizationLocalStore()
    const token = authStorage.get() ?? null
    this.configuration.headers['Authorization'] = `Token ${token}`
    this.instance.interceptors.response.use(undefined, sessionErrorHandler)
    return this
  }

  withQueryParameters (params: Record<string, unknown>): IAPIServiceRequest {
    this.configuration.params = new URLSearchParams(getUrlQueryParameterFromObject(params, false) ?? '')
    return this
  }

  withHeaders (headers: Record<string, string>): IAPIServiceRequest {
    this.configuration.headers = {
      ...this.configuration.headers,
      ...headers,
    }
    return this
  }

  withResponseType (responseType: ResponseType): IAPIServiceRequest {
    this.configuration['responseType'] = responseType
    return this
  }

  async head (url: string): Promise<APIServiceResponse> {
    const response = await this.instance.head(url, this.configuration)
    return { data: undefined, status: response.status, headers: response.headers }
  }

  async get<T> (url: string): Promise<APIServiceResponse<T>> {
    const response = await this.instance.get<T>(url, this.configuration)
    return { data: response.data, status: response.status, headers: response.headers }
  }

  async post<T, R = T, D = T> (url: string, data?: D): Promise<APIServiceResponse<R>> {
    const response = await this.instance.post<T, AxiosResponse<R>, D>(url, data, this.configuration)
    return { data: response.data, status: response.status, headers: response.headers }
  }

  async patch<T, R = T, D = Partial<T>> (url: string, data: D | undefined): Promise<APIServiceResponse<R>> {
    const response = await this.instance.patch<T, AxiosResponse<R>, D>(url, data, this.configuration)
    return { data: response.data, status: response.status, headers: response.headers }
  }

  async put<T, R = T, D = T> (url: string, data: D | undefined): Promise<APIServiceResponse<R>> {
    const response = await this.instance.put<T, AxiosResponse<R>, D>(url, data, this.configuration)
    return { data: response.data, status: response.status, headers: response.headers }
  }

  async delete <T, R = T, D = T> (url: string, data: D | undefined): Promise<APIServiceResponse<R>> {
    const response = await this.instance.delete(url, {
      ...this.configuration,
      data,
    })
    return { data: response.data, status: response.status, headers: response.headers }
  }

}

const apiService: IAPIService = {
  setGlobalConfiguration: (config: APIServiceConfiguration): void => {
    configuration = config
  },
  buildRequest: (): IAPIServiceRequest => {
    if (!configuration) {
      throw new APIServiceConfigurationError('[APIService]: create was called without configuring the APIService')
    }
    return new APIServiceRequest(configuration.baseURL, {
      'Accept-Language': getLanguage(),
      'Accept-Version': configuration.backendVersion,
    })
  },
}

export default apiService
